/**
 * Harness 非流式资源接口：会话、消息和 Run 的查询与控制。
 * 所有请求经由 client.ts，以统一使用 Agent Bearer 凭据和 401 重试策略。
 */
import {
  asNonEmptyString,
  createHarnessApiError,
  createHarnessQuery,
  createHarnessRequestId,
  harnessFetch,
  readHarnessEnvelope,
} from './client'
import { parseHarnessRunView } from './mappers'
import type {
  AssistantConversationPage,
  AssistantConversationQuery,
  AssistantConversationSummary,
  AssistantMessage,
  AssistantMessagePage,
  AssistantMessageQuery,
  HarnessCancelResult,
  HarnessFetchInit,
  HarnessRunEvent,
  HarnessRunEventPage,
  HarnessRunView,
} from './types'

export async function listAssistantConversations(
  query: AssistantConversationQuery = {},
  fetchImpl?: typeof fetch
): Promise<AssistantConversationPage> {
  const params = createHarnessQuery({
    santi_type: query.santiType,
    lifeform_id: query.lifeformId,
    cursor: query.cursor,
    limit: query.limit,
  })
  const data = await requestHarnessData<unknown>(`/conversations${params}`, {
    fetchImpl,
  })
  const page = data as { items?: unknown; next_cursor?: unknown }
  return {
    items: Array.isArray(page?.items)
      ? page.items.map(parseAssistantConversationSummary)
      : [],
    nextCursor: asNonEmptyString(page?.next_cursor) || null,
  }
}

export async function listAssistantMessages(
  conversationId: string,
  query: AssistantMessageQuery = {},
  fetchImpl?: typeof fetch
): Promise<AssistantMessagePage> {
  const params = createHarnessQuery({
    include_tools: query.includeTools,
    cursor: query.cursor,
    limit: query.limit,
  })
  const data = await requestHarnessData<unknown>(
    `/conversations/${encodeURIComponent(conversationId)}/messages${params}`,
    { fetchImpl }
  )
  const page = data as {
    conversation_id?: unknown
    items?: unknown
    next_cursor?: unknown
  }
  return {
    conversationId: asNonEmptyString(page?.conversation_id),
    items: Array.isArray(page?.items)
      ? page.items.map(parseAssistantMessage)
      : [],
    nextCursor: asNonEmptyString(page?.next_cursor) || null,
  }
}

export async function getAssistantRun(
  runId: string,
  fetchImpl?: typeof fetch
): Promise<HarnessRunView> {
  const data = await requestHarnessData<unknown>(
    `/runs/${encodeURIComponent(runId)}`,
    {
      fetchImpl,
    }
  )
  return parseHarnessRunView(data, '', '')
}

export async function listAssistantRunEvents(
  runId: string,
  afterSeq?: number,
  fetchImpl?: typeof fetch
): Promise<HarnessRunEventPage> {
  const params = createHarnessQuery({ after_seq: afterSeq })
  const data = await requestHarnessData<unknown>(
    `/runs/${encodeURIComponent(runId)}/events${params}`,
    { fetchImpl }
  )
  const page = data as {
    conversation_id?: unknown
    run_id?: unknown
    items?: unknown
  }
  return {
    conversationId: asNonEmptyString(page?.conversation_id),
    runId: asNonEmptyString(page?.run_id),
    items: Array.isArray(page?.items)
      ? page.items.map(parseHarnessRunEvent)
      : [],
  }
}

export async function resumeAssistantRun(
  runId: string,
  fetchImpl?: typeof fetch
): Promise<HarnessRunView> {
  const data = await requestHarnessData<unknown>(
    `/runs/${encodeURIComponent(runId)}/resume`,
    {
      method: 'POST',
      body: { request_id: createHarnessRequestId() },
      headers: { 'Content-Type': 'application/json' },
      fetchImpl,
    }
  )
  return parseHarnessRunView(data, '', '')
}

export async function cancelAssistantRun(
  runId: string,
  reason?: string,
  fetchImpl?: typeof fetch
): Promise<HarnessCancelResult> {
  const body: { request_id: string; reason?: string } = {
    request_id: createHarnessRequestId(),
  }
  if (reason !== undefined) {
    body.reason = reason
  }

  const data = await requestHarnessData<unknown>(
    `/runs/${encodeURIComponent(runId)}/cancel`,
    {
      method: 'POST',
      body,
      headers: { 'Content-Type': 'application/json' },
      fetchImpl,
    }
  )
  const result = data as {
    conversation_id?: unknown
    run_id?: unknown
    status?: unknown
    state_version?: unknown
  }
  return {
    conversationId: asNonEmptyString(result?.conversation_id),
    runId: asNonEmptyString(result?.run_id),
    status: asNonEmptyString(result?.status),
    stateVersion:
      typeof result?.state_version === 'number' ? result.state_version : 0,
  }
}

async function requestHarnessData<T>(
  path: string,
  init: HarnessFetchInit = {}
): Promise<T> {
  const response = await harnessFetch(path, init)
  const parsed = await readHarnessEnvelope<T>(response)
  const envelope = parsed.envelope
  if (!envelope || envelope.success !== true) {
    throw createHarnessApiError(response.status, envelope, parsed.raw)
  }
  return envelope.data
}

function parseAssistantConversationSummary(
  value: unknown
): AssistantConversationSummary {
  const item = value as {
    conversation_id?: unknown
    lifeform_id?: unknown
    title?: unknown
    updated_at?: unknown
    is_pinned?: unknown
  }
  return {
    conversationId: asNonEmptyString(item?.conversation_id),
    lifeformId: asNonEmptyString(item?.lifeform_id),
    title: typeof item?.title === 'string' ? item.title : '',
    updatedAt: asNonEmptyString(item?.updated_at),
    isPinned: item?.is_pinned === true,
  }
}

function parseAssistantMessage(value: unknown): AssistantMessage {
  const item = value as {
    id?: unknown
    role?: unknown
    content?: unknown
    created_at?: unknown
    source?: unknown
  }
  return {
    id: asNonEmptyString(item?.id),
    role: asNonEmptyString(item?.role),
    content:
      typeof item?.content === 'string'
        ? item.content
        : item?.content === undefined
        ? ''
        : JSON.stringify(item.content),
    createdAt: asNonEmptyString(item?.created_at),
    source: asNonEmptyString(item?.source),
  }
}

function parseHarnessRunEvent(value: unknown): HarnessRunEvent {
  const item = value as {
    event_seq?: unknown
    event_key?: unknown
    type?: unknown
    data?: unknown
  }
  return {
    eventSeq: typeof item?.event_seq === 'number' ? item.event_seq : 0,
    eventKey: asNonEmptyString(item?.event_key),
    type: asNonEmptyString(item?.type),
    data: item?.data,
  }
}
